/* ------------------------------------------------------------------ */
//  Limitless Traders — Lightweight Analytics
//  No external dependency. Routes to console by default.
//  Swap the dispatchers below to integrate Mixpanel, Amplitude, GA4, etc.
/* ------------------------------------------------------------------ */

type EventName =
  | "page_view"
  | "auth_signup"
  | "auth_login"
  | "auth_logout"
  | "course_start"
  | "lesson_complete"
  | "video_play"
  | "video_progress"
  | "journal_entry_create"
  | "journal_entry_view"
  | "ai_coach_message"
  | "subscription_view"
  | "subscription_purchase_start"
  | "subscription_purchase_complete"
  | "push_notification_subscribe"
  | "push_notification_click"
  | "error_caught";

interface EventProps {
  [key: string]: string | number | boolean | null | undefined;
}

const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
let USER_ID: string | null = null;

/* ------------------------------------------------------------------ */
//  Dispatchers
/* ------------------------------------------------------------------ */
function consoleDispatcher(event: EventName, props: EventProps) {
  if (import.meta.env.PROD) return; // Only log in dev
  console.log(`[Analytics] ${event}`, { session: SESSION_ID, ...props });
}

async function edgeDispatcher(event: EventName, props: EventProps) {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine) {
      await fetch("/functions/v1/log-analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event,
          props: { session_id: SESSION_ID, user_id: USER_ID, ...props },
          timestamp: new Date().toISOString(),
        }),
        keepalive: true,
      });
    }
  } catch {
    // silent in production
  }
}

/* ------------------------------------------------------------------ */
//  Public API
/* ------------------------------------------------------------------ */
export function setAnalyticsUser(userId: string | null) {
  USER_ID = userId;
}

export function track(event: EventName, props?: EventProps) {
  const payload = props || {};
  // Always dispatch to console in dev
  consoleDispatcher(event, payload);
  // Dispatch to edge in production
  if (import.meta.env.PROD) {
    edgeDispatcher(event, payload).catch(() => {});
  }
}

export function trackPageView(path: string, title?: string) {
  track("page_view", { path, title: title || document.title });
}

/* ------------------------------------------------------------------ */
//  React Router integration helper
//  Usage: useEffect(() => trackPageView(location.pathname), [location])
/* ------------------------------------------------------------------ */
export function usePageTracking() {
  // This is a placeholder — import and use in App.tsx or a layout component
}
